import type { AfterViewInit, OnDestroy, OnInit } from '@angular/core';
import { Component, inject, Renderer2 } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import type { NavigationExtras } from '@angular/router';
import { NavigationEnd, Router } from '@angular/router';

import { LetDirective } from '@ngrx/component';
import { DynamicModule } from 'ng-dynamic-component';
import type { Observable } from 'rxjs';
import {
	combineLatest,
	filter,
	map,
	of,
	shareReplay,
	Subject,
	switchMap,
	takeUntil,
	tap,
} from 'rxjs';

import { CookieBannerComponent } from '@valk-nx/compositions/ui-cookie-banner/src/lib/cookie-banner';
import type { TranslatedSlug } from '@valk-nx/compositions/ui-header/src/lib/header.interface';
import type { Language } from '@valk-nx/core/lib/core';
import { cn, HOTEL_SLUG, LANGUAGE } from '@valk-nx/core/lib/core';
import { BannerComponent as SbBannerComponent } from '@valk-nx/storyblok/components/banner/src/lib/banner';
import { FooterComponent as SbFooterComponent } from '@valk-nx/storyblok/components/footer/src/lib/footer';
import { HeaderComponent } from '@valk-nx/storyblok/components/header/src/lib/header';
import { StoryblokHelper } from '@valk-nx/storyblok-helpers/src/lib/general/storyblok.helper';
import { ContentService } from '@valk-nx/storyblok-services/src/lib/services/content.service';
import { MetadataService } from '@valk-nx/storyblok-services/src/lib/services/metadata.service';
import { StoryblokService } from '@valk-nx/storyblok-services/src/lib/services/storyblok.service';
import { HotelStore } from '@valk-nx/storyblok-store/src/lib/store/hotel/hotel.store';
import { PageSettingsStore } from '@valk-nx/storyblok-store/src/lib/store/page-settings/page-settings.store';
import type { StoryblokGlobals } from '@valk-nx/storyblok-types/src/lib/types/storyblok.interface';

const Components = {
	banner: SbBannerComponent,
	footer: SbFooterComponent,
	header: HeaderComponent,
};

@Component({
	selector: 'sb-wrapper',
	templateUrl: './wrapper.component.html',
	imports: [DynamicModule, LetDirective, CookieBannerComponent],
	providers: [HotelStore, PageSettingsStore],
})
export class SbWrapperComponent implements AfterViewInit, OnDestroy, OnInit {
	private readonly sbService = inject(StoryblokService);
	private readonly contentService = inject(ContentService);
	private readonly metadataService = inject(MetadataService);
	private readonly router = inject(Router);
	private readonly hotelSlug = inject(HOTEL_SLUG);
	private readonly renderer2 = inject(Renderer2);
	private readonly language = inject<Language>(LANGUAGE);

	readonly hotelStore = inject(HotelStore);
	readonly pageSettingsStore = inject(PageSettingsStore);

	readonly cn = cn;

	wrapper$!: Observable<{
		header: StoryblokGlobals['header'] & {
			translatedSlugs?: TranslatedSlug[];
		};
		banner: StoryblokGlobals['banner'];
		footer: StoryblokGlobals['footer'];
		language: Language;
	}>;
	components: typeof Components = Components;
	storyblokComponent = '';

	destroy$ = new Subject<void>();

	hideMenu: Observable<boolean>;

	constructor() {
		this.hideMenu = toObservable(this.pageSettingsStore.hideMenu);

		/**
		 * Page settings are derived here, not in the routed page: the chrome
		 * renders above `<ng-content />`, so a value pushed from below lands a
		 * change-detection cycle too late and the header paints with the
		 * previous page's value first. Registering before the template
		 * subscribes keeps this ahead of `wrapper$`, which cannot emit until
		 * `story$` has (`translatedSlugs$` derives from it) — so the header's
		 * first render already has the right value on server and client alike.
		 */
		this.sbService.story$.pipe(takeUntilDestroyed()).subscribe((story) => {
			const isMenuless = story.content.component === 'menulessPage';

			this.pageSettingsStore.updateState({
				hideMenu: isMenuless,
				hideFooter: isMenuless,
				negativeHeader: StoryblokHelper.isNegativeHeader(story.content),
			});
		});
	}

	ngOnInit() {
		this.wrapper$ = combineLatest([
			this.sbService.globals$,
			this.sbService.translatedSlugs$,
			this.hideMenu,
		]).pipe(
			takeUntil(this.destroy$),
			map(([sbGlobals, translatedSlugs, hideMenu]) => {
				const wrapper = {
					...sbGlobals,
				};
				wrapper.header['translatedSlugs'] = translatedSlugs;
				wrapper.header.hideMenu = hideMenu;

				wrapper.language = (
					translatedSlugs?.find((slug) => slug.selected)?.lang ??
					this.language
				).toLowerCase() as Language;
				return wrapper;
			}),
			switchMap((wrapper) => {
				this.metadataService.setSiteName();

				if (this.hotelSlug) {
					return this.contentService
						.getHotelInfo(wrapper.language)
						.pipe(
							tap((hotelData) => {
								this.metadataService.setSchemaOrgHotel(
									this.renderer2,
									hotelData,
								);
								this.hotelStore.updateState(hotelData);
							}),
							map(() => wrapper),
							shareReplay(1),
						);
				} else {
					this.metadataService.setDefaultSchema(this.renderer2);
					return of(wrapper);
				}
			}),
		);
	}

	ngOnDestroy() {
		this.destroy$.next();
		this.destroy$.complete();
	}

	ngAfterViewInit() {
		this.router.events
			.pipe(
				filter((event) => event instanceof NavigationEnd),
				takeUntil(this.destroy$),
			)
			.subscribe(() => {
				const urlTree = this.router.parseUrl(this.router.url);
				const primarySegment =
					urlTree.root.children['primary']?.segments[0]?.path;

				/* v8 ignore start */
				if (primarySegment === this.language) {
					/* v8 ignore stop */
					// Get the segments after the language segment
					const segments =
						urlTree.root.children['primary']?.segments.slice(1) ??
						[];
					const newPath =
						'/' + segments.map((segment) => segment.path).join('/');

					const navigationExtras: NavigationExtras = {};
					if (Object.keys(urlTree.queryParams).length > 0) {
						navigationExtras.queryParams = urlTree.queryParams;
					}
					if (urlTree.fragment) {
						navigationExtras.fragment = urlTree.fragment;
					}

					this.router.navigate([newPath], navigationExtras);
				}

				this.storyblokComponent = urlTree.queryParams['_storyblok_c'];
			});
	}
}
